import { formatDataValue, formatDateWithTime } from './format';
import {
  SaveLeadPayload,
  SingleLead,
  SingleLeadOverviewResponse,
} from '@/store/modules/leads/types';
import { Lead, LeadFormField } from '@/types/Leads';

export const evaluateConditions = (
  conditions: string,
  leadFormFields: LeadFormField[]
): boolean => {
  // Regex to deconstruct condition string
  const regex = /(?<link>&&|\|\|)?\s*(?<section>\w+)\.(?<key>\w+)\s*==\s*(?<value>\w+)/g;

  let result: boolean | null = null;

  let match: RegExpExecArray | null;
  while ((match = regex.exec(conditions)) !== null) {
    if (match != null && match.groups != undefined) {
      const groups = match.groups;
      // Find FormField referenced in the condition by section and key
      const referencedField = leadFormFields.find(field => {
        return field.section == groups.section && field.key == groups.key;
      });

      if (referencedField != null) {
        if (result == null) {
          // The first condition has no link so we will just use the boolean result as result
          result = referencedField.value == groups.value;
        } else {
          // All following conditions need to be linked to the previous by either && (AND) or || (OR)
          if (groups.link != undefined) {
            if (groups.link == '&&') {
              result = result && referencedField.value == groups.value;
            } else if (groups.link == '||') {
              result = result || referencedField.value == groups.value;
            } else {
              throw new SyntaxError(
                'FormField condition can not be parsed. All conditions need to be joined with && or ||.'
              );
            }
          } else {
            throw new SyntaxError(
              'FormField condition can not be parsed. All conditions need to be joined with && or ||.'
            );
          }
        }
      } else {
        throw new SyntaxError(
          'FormField condition can not be parsed. Referenced Field in condition can not be found in form.'
        );
      }
    }
  }

  return result !== null ? result : false; // Return final result or false if no conditions were matched
};

export const createLeads = (leads: SingleLeadOverviewResponse[]) => {
  return leads.map(
    lead =>
      ({
        clientType: formatDataValue(lead.clientType),
        bankerId: lead.bankerId.dataValue
          ? formatDataValue(lead.bankerId)
          : '-',
        dateAdded: formatDataValue(lead.dateAdded, {
          overrideDateFormat: true,
        }),
        scheduledDate: lead.scheduledDate.dataValue
          ? formatDataValue(lead.scheduledDate)
          : '-',
        dateDue: lead.dateDue.dataValue
          ? formatDateWithTime(lead.dateDue)
          : '-',
        funnel: formatDataValue(lead.funnel),
        xPlanId: lead.xPlanId.dataValue ? formatDataValue(lead.xPlanId) : null,
        utmCampaign: formatDataValue(lead.utmCampaign),
        interactionsNumber: formatDataValue(lead.interactionsNumber),
        leadId: formatDataValue(lead.leadId),
        leadStatus: formatDataValue(lead.leadStatus),
        manualStatus: formatDataValue(lead.manualStatus),
        location: formatDataValue(lead.location),
        responsibleUserId: lead.responsibleUserId.dataValue
          ? formatDataValue(lead.responsibleUserId)
          : '-',
        taskId: formatDataValue(lead.taskId),
        taskStatus: formatDataValue(lead.taskStatus),
        investmentServices: formatDataValue(lead.investmentServices),
        probabilityOfSale: formatDataValue(lead.probabilityOfSale),
        expectedAum: formatDataValue(lead.expectedAum),
        competitorAum: formatDataValue(lead.competitorAum),
        lastInteraction: lead.lastInteraction.dataValue
          ? formatDateWithTime(lead.lastInteraction)
          : '',
      } as Lead)
  );
};

export const formatSavePayload = (lead: SingleLead): SaveLeadPayload => {
  return {
    clientType: lead.clientType.dataValue || null,
    interestedInWealthPlanning:
      lead.interestedInWealthPlanning.dataValue || null,
    intermediaryType: lead.intermediaryType.dataValue || null,
    source: lead.source.dataValue || null,
    sourceComments: lead.sourceComments.dataValue || null,
    serviceOfInterest:
      lead.serviceOfInterest.map(service => service.dataValue) || null,
    typeOfAdvice: lead.typeOfAdvice.dataValue || null,
    alignmentToRiskProfileTool:
      lead.alignmentToRiskProfileTool.dataValue || null,
    thirdPartyPlatform: lead.thirdPartyPlatform.dataValue || null,
    personaType: lead.personaType.dataValue || null,
    expectedAum: lead.expectedAum.dataValue || null,
    competitorAum: lead.competitorAum.dataValue || null,
    investmentExperience: lead.investmentExperience.dataValue || null,
    competitorName: lead.competitorName.dataValue || null,
    extra1CompetitorAum: lead.extra1CompetitorAum.dataValue || null,
    extra1CompetitorName: lead.extra1CompetitorName.dataValue || null,
    extra2CompetitorAum: lead.extra2CompetitorAum.dataValue || null,
    extra2CompetitorName: lead.extra2CompetitorName.dataValue || null,
    investmentPurpose: lead.investmentPurpose.dataValue || null,
    reasonToSwitch: lead.reasonToSwitch.dataValue || null,
    residency: lead.residency.dataValue || null,
    contactNotes: lead.contactNotes.dataValue || null,
    bankerId: lead.bankerId.dataValue || null,
    secondBankerId: lead.secondBankerId.dataValue || null,
    nextInteraction: {
      interactionDate: lead.nextInteractionDate.dataValue || null,
      interactionTime: lead.nextInteractionTime.dataValue || null,
      interactionType: lead.channel.dataValue || null,
    },
    meetingOutcome: {
      manualStatus: lead.manualStatus.dataValue || null,
      probabilityOfSale: lead.probabilityOfSale.dataValue || null,
      aumInflow: lead.aumInflow.dataValue || null,
      auaExpected: lead.auaExpected.dataValue || null,
      adviceFeeOneOff: lead.adviceFeeOneOff.dataValue || null,
      adviceFeeRecurringPercent:
        lead.adviceFeeRecurringPercent.dataValue || null,
      serviceType: lead.serviceType.dataValue || null,
      feesPct: lead.fees.dataValue || null,
    },
  } as SaveLeadPayload;
};
